/* eslint-disable no-param-reassign */
/* eslint-disable react/no-unknown-property */
/* eslint-disable react/no-danger */
import { json } from '@remix-run/node';
import {
  Links,
  LiveReload,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  useLoaderData,
} from '@remix-run/react';
import {
  HydrationBoundary,
  QueryClient,
  QueryClientProvider,
} from '@tanstack/react-query';
import { ElemasonRemixProvider } from '@tectonic/elemason-remix';
import { ThemeProvider } from '@tectonic/uikit';
import {
  populate,
  populateEnvs,
  isMobile as queryIsMobile,
} from '@tectonic/utils';
import axios from 'axios';
import { isEmpty } from 'lodash-es';
import { memo, useEffect, useState } from 'react';
import { useDehydratedState } from 'use-dehydrated-state';
import Transition from './components/Transition';
import { RootProvider } from './contexts/RootContext';
import useAppLaunchEffect from './hooks/useAppLaunchEffect';

import type { AppLoadContext, LoaderFunctionArgs } from '@remix-run/node';
import type { Routes } from '@tectonic/elemason-remix';
import type { Color, ElemasonWidget, User } from '@tectonic/types';
import type { ComponentType, FC, ReactNode } from 'react';

interface AppProps {
  routes: Routes;
  title?: string;
  cssHref: string;
  description?: string;
  colors: Record<Color, string>;
  // extra children to be added to the head
  head?: ReactNode;
  body?: ReactNode;
  widgets?: Record<
    string,
    ComponentType<{ widget: ElemasonWidget<unknown, unknown, unknown> }>
  >;
}

const minifyCSS = (css: string) => {
  // Remove comments
  css = css.replace(/\/\*[\s\S]*?\*\//g, '');

  // Remove newline characters
  css = css.replace(/\n/g, '');

  // Remove whitespace before and after colons
  css = css.replace(/\s*:\s*/g, ':');

  // Remove whitespace before and after semicolons
  css = css.replace(/\s*;\s*/g, ';');

  // Remove whitespace before and after commas
  css = css.replace(/\s*,\s*/g, ',');

  // Remove whitespace before and after curly braces
  css = css.replace(/\s*{\s*/g, '{');
  css = css.replace(/\s*}\s*/g, '}');

  // Remove whitespace before and after parentheses
  css = css.replace(/\s*\(\s*/g, '(');
  css = css.replace(/\s*\)\s*/g, ')');

  return css;
};

const getBrowserEnv = (context: AppLoadContext) => {
  const env = context.browserEnvs as Record<string, string>;
  const { GTAG_HOST, GTAG_ID, ORGANIZATION, API_HOST } = env;
  const placeholders = {
    organization: ORGANIZATION,
    gtmHost: GTAG_HOST,
    gtagId: GTAG_ID,
    apiHost: API_HOST,
  };

  return {
    ORGANIZATION: env.ORGANIZATION,
    NODE_ENV: process.env.NODE_ENV,

    MIXPANEL_PROJECT_TOKEN: env.MIXPANEL_PROJECT_TOKEN,
    MIXPANEL_PROXY_DOMAIN: env.MIXPANEL_PROXY_DOMAIN,
    ENABLE_FP_PRO: env.ENABLE_FP_PRO,

    GTAG_HOST,
    GTAG_ID,
    GTAG_JS_URL: populate(env.GTAG_JS_URL, placeholders),

    FIREBASE_APP_ID: env.FIREBASE_APP_ID,
    FIREBASE_API_KEY: env.FIREBASE_API_KEY,
    FIREBASE_PROJECT_ID: env.FIREBASE_PROJECT_ID,
    FIREBASE_AUTH_DOMAIN: env.FIREBASE_AUTH_DOMAIN,
    FIREBASE_STORAGE_BUCKET: env.FIREBASE_STORAGE_BUCKET,
    FIREBASE_MEASUREMENT_ID: env.FIREBASE_MEASUREMENT_ID,
    FIREBASE_MESSAGING_SENDER_ID: env.FIREBASE_MESSAGING_SENDER_ID,

    DAM_URL: populate(env.DAM_URL, placeholders),
    API_END_POINT_V2: populate(env.API_END_POINT_V2, placeholders),
    ROOT_DOMAIN: env.ROOT_DOMAIN,
    SENTRY_DSN: env.SENTRY_DSN,
    MIXPANEL_DEBUG_MODE: env.MIXPANEL_DEBUG_MODE,

    CLARITY_TOKEN: env.CLARITY_TOKEN,
  };
};

let cachedCss = '';

const loaderGen =
  (mainCss: string) =>
  async ({ context, request }: LoaderFunctionArgs) => {
    populateEnvs(process.env);

    const env = getBrowserEnv(context);
    const isMobile = queryIsMobile(request.headers.get('user-agent'));

    const css =
      cachedCss ||
      ((await axios.get(`${new URL(request.url).origin}${mainCss}`))
        .data as string);

    cachedCss = css;

    return json({
      env,
      isMobile,
      css: minifyCSS(css),
      serverTime: Date.now(),
      currentUser: (context.auth as Record<string, unknown>)
        .currentUser as User,
    });
  };

const loader = async ({ context, request }: LoaderFunctionArgs) => {
  populateEnvs(process.env);

  const env = getBrowserEnv(context);
  const isMobile = queryIsMobile(request.headers.get('user-agent'));

  return json({
    env,
    isMobile,
    css: '',
    serverTime: Date.now(),
    currentUser: (context.auth as Record<string, unknown>).currentUser as User,
  });
};

type RootLoaderType = Awaited<
  ReturnType<Awaited<ReturnType<typeof loader>>['json']>
>;

// TODO: Delete this package and make it part of the application code itself.
// - We need to setup analytics. Analytics requires it's own store. If we keep core
// as a separate we'll have to add elemason as dependency to this project and import
// functions like toAnalyticsPayload etc.
// - we need route handle data so that we can pass arbitrary data to component
// eg. analytics requires screen name. Presently, we dump screen name to analytic
// store.
// - Currently, remix is not part of the elemason. Because of that when we a user
// navigates from one screen to another, entire page is reloaded since we are
// using `window.location.href=something`. When we use useNavigation from
// remix, it doesn't reload the entire page.

const getFps = () =>
  [
    window.localStorage.getItem('ttClientFpV0'),
    window.localStorage.getItem('fingerPrintV3') &&
      JSON.parse(window.localStorage.getItem('fingerPrintV3') || '{}')
        .visitorId,
    window.localStorage.getItem('fingerPrintPro') &&
      JSON.parse(window.localStorage.getItem('fingerPrintPro') || '{}')
        .visitorId,
  ].filter((fp) => !isEmpty(fp));

const FingerPrints = memo(() => {
  const [fingerprints, setFingerprints] = useState<any[]>([]);
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (!params.has('ttfp')) {
      return;
    }
    const fps = getFps();
    if (!isEmpty(fps)) {
      setFingerprints(fps);
    } else {
      setTimeout(() => {
        setFingerprints(getFps());
      }, 1500);
    }
  }, []);

  if (isEmpty(fingerprints)) {
    return null;
  }

  return (
    <div
      id="ttPandoraFingerprintDebugCont"
      style={{
        position: 'fixed',
        top: 0,
        right: 0,
        padding: '2px 10px',
        border: '1px solid #7f7f7f21',
        width: '100%',
        backgroundColor: '#24242be0',
        margin: '0 auto',
        color: '#bebeca',
        textAlign: 'center',
        overflow: 'hidden',
        boxShadow: '0px 10px 10px grey',
        zIndex: '9999',
      }}
      className="flex flex-col"
    >
      <button
        type="button"
        style={{
          float: 'right',
          position: 'absolute',
          top: '0px',
          right: '2px',
          padding: '5px',
          color: 'whitesmoke',
          fontSize: 'large',
        }}
        onClick={() => setFingerprints([])}
      >
        &#x2716;
      </button>
      Fingerprints <br />
      {
        // eslint-disable-next-line react/no-array-index-key
        fingerprints.map((fp, idx) => (
          <div key={`fp-${idx}`}>{fp}</div>
        ))
      }
    </div>
  );
});

const App: FC<AppProps> = ({
  colors,
  routes,
  title,
  cssHref,
  description,
  head,
  widgets,
  body,
}) => {
  const dehydratedState = useDehydratedState();
  const [queryClient] = useState(() => new QueryClient());
  const { env, currentUser, isMobile, serverTime, css } =
    useLoaderData<RootLoaderType>();

  useEffect(() => {
    globalThis.timeDrift = Date.now() - serverTime;
  }, []);

  useAppLaunchEffect({
    env,
    currentUser,
    isMobile,
    serverTime,
  });

  return (
    <html lang="en">
      <head>
        <meta charSet="utf-8" />
        <link rel="preconnect" href={env.DAM_URL} />
        <link rel="preconnect" href="https://c.clarity.ms" />
        <link rel="preconnect" href="https://c.bing.com" />
        <meta
          name="viewport"
          content="width=device-width, initial-scale=1, maximum-scale=5"
        />
        <title>{title}</title>
        <meta property="og:title" content={title} />
        <meta name="description" content={description} />
        <meta property="og:description" content={description} />
        {css ? (
          <style>{css}</style>
        ) : (
          cssHref && (
            <link
              suppressHydrationWarning
              // @ts-ignore
              fetchpriority="high"
              rel="stylesheet"
              href={cssHref}
              as="style"
            />
          )
        )}
        <Meta />
        <Links />
        <script
          dangerouslySetInnerHTML={{
            __html: `window.env = JSON.parse('${JSON.stringify(
              env
            )}');window.window.addEventListener('scroll', () => {
              document.body.style.setProperty('--scroll-y', window.scrollY)
            });console.error = function() {};`,
          }}
        />
        {!isEmpty(env.CLARITY_TOKEN) && (
          <script
            dangerouslySetInnerHTML={{
              __html: `
            (function(c,l,a,r,i,t,y){
              c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};
              t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;
              y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);
            })(window, document, "clarity", "script", "${env.CLARITY_TOKEN}");`,
            }}
          />
        )}
        {head ?? null}
      </head>
      {/* @ts-ignore */}
      <body className="transition-all" style={{ '--scroll-y': 0 }}>
        <FingerPrints />
        <RootProvider value={{ env, currentUser, isMobile }}>
          <Transition />
          <ThemeProvider
            value={{
              colors,
              icon: { spinner: 'outline-spinner', sprite: '/main.svg' },
            }}
          >
            <ElemasonRemixProvider
              value={{ user: currentUser as User, env, routes, widgets }}
            >
              <QueryClientProvider client={queryClient}>
                <HydrationBoundary state={dehydratedState}>
                  <Outlet />
                </HydrationBoundary>
              </QueryClientProvider>
            </ElemasonRemixProvider>
          </ThemeProvider>
        </RootProvider>
        <ScrollRestoration />
        <Scripts />
        <LiveReload />
        {/* <ReactQueryDevtools initialIsOpen={false} /> */}
        {body}
      </body>
    </html>
  );
};

export { App as RootApp, loaderGen, loader as rootLoader };
export type { RootLoaderType };
